import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Reveal from './Reveal';
import useJsonLd from '../hooks/useJsonLd';

/**
 * FAQ section — renders an accordion of question/answer pairs AND emits
 * FAQPage JSON-LD structured data. Critical for AEO (featured snippets,
 * People Also Ask, Google AI Overviews) and LLMO (ChatGPT/Perplexity citations).
 *
 * Props:
 *   - items: [{ q, a }]
 *   - schemaId: unique id for the JSON-LD script tag (page-scoped)
 *   - eyebrow, title, subtitle: visual header text
 */
export default function FAQSection({
  items = [],
  schemaId = 'faq-page-schema',
  eyebrow = 'FAQ',
  title = 'Frequently Asked Questions',
  subtitle,
}) {
  const [openIdx, setOpenIdx] = useState(0);

  const faqSchema = items.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((it) => ({
          '@type': 'Question',
          name: it.q,
          acceptedAnswer: { '@type': 'Answer', text: it.a },
        })),
      }
    : null;

  useJsonLd(schemaId, faqSchema);

  if (!items.length) return null;

  return (
    <section className='py-24 relative' data-testid='faq-section'>
      <div className='max-w-4xl mx-auto px-6 relative z-10'>
        <div className='text-center mb-12'>
          <Reveal>
            <div className='eyebrow-pill'>{eyebrow}</div>
            <h2 className='mt-6 font-heading text-4xl md:text-5xl font-bold text-white leading-[1.05]'>{title}</h2>
            {subtitle && (
              <p className='mt-5 text-white/65 text-lg max-w-2xl mx-auto font-body'>{subtitle}</p>
            )}
          </Reveal>
        </div>

        <div className='space-y-3' itemScope itemType='https://schema.org/FAQPage'>
          {items.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div
                key={idx}
                data-testid={`faq-item-${idx}`}
                itemScope
                itemProp='mainEntity'
                itemType='https://schema.org/Question'
                className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 ${
                  isOpen ? 'ring-1 ring-brand/40' : ''
                }`}
              >
                <button
                  type='button'
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className='w-full flex items-center justify-between gap-5 px-6 py-5 text-left'
                  data-testid={`faq-toggle-${idx}`}
                  aria-expanded={isOpen}
                >
                  <h3
                    itemProp='name'
                    className='font-heading text-[17px] md:text-[19px] font-semibold text-white leading-snug'
                  >
                    {item.q}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-brand shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  itemScope
                  itemProp='acceptedAnswer'
                  itemType='https://schema.org/Answer'
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className='overflow-hidden'>
                    <p
                      itemProp='text'
                      className='px-6 pb-6 text-white/75 text-[15.5px] leading-[1.75] font-body'
                    >
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
